// === GESTIONNAIRE DE RESSOURCES ===
// 5 types de ressources persistantes (localStorage).
// Alimentent le craft et les upgrades de bâtiments.

import { LifetimeStats } from './LifetimeStats';

const STORAGE_KEY = 'deadtide_resources';

/** Types de ressources */
export type ResourceType = 'wood' | 'iron' | 'cloth' | 'crystal' | 'cursed_bone';

/** Labels et emojis pour l'affichage */
export const RESOURCE_INFO: Record<ResourceType, { emoji: string; name: string; rarity: 'common' | 'rare' }> = {
  wood:        { emoji: '🪵', name: 'Bois',       rarity: 'common' },
  iron:        { emoji: '⛏️', name: 'Fer',        rarity: 'common' },
  cloth:       { emoji: '🧵', name: 'Tissu',      rarity: 'common' },
  crystal:     { emoji: '💎', name: 'Cristal',    rarity: 'rare' },
  cursed_bone: { emoji: '💀', name: 'Os Maudit',  rarity: 'rare' },
};

export const ALL_RESOURCES: ResourceType[] = ['wood', 'iron', 'cloth', 'crystal', 'cursed_bone'];
export const COMMON_RESOURCES: ResourceType[] = ['wood', 'iron', 'cloth'];

/** Stock de ressources */
export type ResourceStock = Record<ResourceType, number>;

const DEFAULT_STOCK: ResourceStock = {
  wood: 0,
  iron: 0,
  cloth: 0,
  crystal: 0,
  cursed_bone: 0,
};

export class ResourceManager {
  private stock: ResourceStock;

  constructor() {
    this.stock = this.load();
  }

  // === ACCÈS ===

  getAll(): ResourceStock {
    return { ...this.stock };
  }

  get(type: ResourceType): number {
    return this.stock[type];
  }

  // === MODIFICATION ===

  /** Ajouter des ressources */
  add(type: ResourceType, amount: number): void {
    this.stock[type] = Math.max(0, this.stock[type] + amount);
    if (amount > 0) LifetimeStats.addResource(type, amount);
    this.save();
  }

  /** Ajouter plusieurs ressources d'un coup */
  addMultiple(resources: Partial<ResourceStock>): void {
    for (const [type, amount] of Object.entries(resources)) {
      if (amount && amount > 0) {
        this.stock[type as ResourceType] += amount;
      }
    }
    LifetimeStats.addResources(resources);
    this.save();
  }

  /** Dépenser des ressources (retourne false si pas assez) */
  spend(cost: Partial<ResourceStock>): boolean {
    if (!this.canAfford(cost)) return false;
    for (const [type, amount] of Object.entries(cost)) {
      if (amount && amount > 0) {
        this.stock[type as ResourceType] -= amount;
      }
    }
    this.save();
    return true;
  }

  /** Vérifie si on a assez de ressources */
  canAfford(cost: Partial<ResourceStock>): boolean {
    for (const [type, amount] of Object.entries(cost)) {
      if (amount && amount > 0 && this.stock[type as ResourceType] < amount) {
        return false;
      }
    }
    return true;
  }

  // === PERSISTANCE ===

  private save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.stock));
    } catch {
      // localStorage plein ou indisponible
    }
  }

  private load(): ResourceStock {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        return { ...DEFAULT_STOCK, ...data };
      }
    } catch {
      // données corrompues
    }
    return { ...DEFAULT_STOCK };
  }

  /** Reset complet (debug) */
  resetAll(): void {
    this.stock = { ...DEFAULT_STOCK };
    this.save();
  }
}
